import apiClient from './apiClient';

export type PermitType =
  | 'nepa_building'
  | 'nepa_environmental'
  | 'fire_safety'
  | 'occupancy'
  | 'demolition'
  | 'subdivision'
  | 'mining'
  | 'utility_connection'
  | 'other';
export type PermitStatus = 'draft' | 'submitted' | 'under_review' | 'approved' | 'active' | 'renewal_pending' | 'expired' | 'closed' | 'rejected';
export type PermitConditionStatus = 'open' | 'in_progress' | 'satisfied' | 'waived';
export type PermitRenewalStatus = 'planned' | 'submitted' | 'approved' | 'rejected' | 'cancelled';

export interface PermitCondition {
  id: string;
  permitId: string;
  conditionCode: string;
  description: string;
  status: PermitConditionStatus;
  dueDate: string | null;
  ownerUserId: string | null;
  evidenceDocumentId: string | null;
  satisfiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PermitRenewal {
  id: string;
  permitId: string;
  renewalNumber: string;
  status: PermitRenewalStatus;
  applicationDate: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  newExpiryDate: string | null;
  documentId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PermitRow {
  id: string;
  projectId: string;
  permitNumber: string;
  title: string;
  permitType: PermitType;
  issuingAuthority: string;
  status: PermitStatus;
  applicationDate: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  responsibleUserId: string | null;
  documentId: string | null;
  conditions?: PermitCondition[];
  renewals?: PermitRenewal[];
  createdAt: string;
  updatedAt: string;
}

export interface PermitPayload {
  projectId: string;
  permitNumber: string;
  title: string;
  permitType: PermitType;
  issuingAuthority: string;
  status?: PermitStatus;
  applicationDate?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  responsibleUserId?: string | null;
  documentId?: string | null;
}

class PermitsService {
  async list(params: { projectId?: string; status?: PermitStatus; permitType?: PermitType; expiresBefore?: string; expiresAfter?: string } = {}): Promise<PermitRow[]> {
    const response: any = await apiClient.get('/permits', { params });
    return response.data;
  }

  async create(payload: PermitPayload): Promise<PermitRow> {
    const response: any = await apiClient.post('/permits', payload);
    return response.data;
  }

  async update(id: string, payload: Partial<PermitPayload>): Promise<PermitRow> {
    const response: any = await apiClient.patch(`/permits/${id}`, payload);
    return response.data;
  }

  async close(id: string): Promise<PermitRow> {
    const response: any = await apiClient.post(`/permits/${id}/close`);
    return response.data;
  }

  async addCondition(permitId: string, payload: { conditionCode: string; description: string; dueDate?: string | null; status?: PermitConditionStatus }): Promise<PermitCondition> {
    const response: any = await apiClient.post(`/permits/${permitId}/conditions`, payload);
    return response.data;
  }

  async updateCondition(conditionId: string, payload: Partial<{ conditionCode: string; description: string; dueDate: string | null; status: PermitConditionStatus }>): Promise<PermitCondition> {
    const response: any = await apiClient.patch(`/permits/conditions/${conditionId}`, payload);
    return response.data;
  }

  async deleteCondition(conditionId: string): Promise<void> {
    await apiClient.delete(`/permits/conditions/${conditionId}`);
  }

  async addRenewal(permitId: string, payload: { renewalNumber: string; status?: PermitRenewalStatus; applicationDate?: string | null; newExpiryDate?: string | null; notes?: string | null }): Promise<PermitRenewal> {
    const response: any = await apiClient.post(`/permits/${permitId}/renewals`, payload);
    return response.data;
  }

  async updateRenewal(renewalId: string, payload: Partial<{ renewalNumber: string; status: PermitRenewalStatus; applicationDate: string | null; newExpiryDate: string | null; notes: string | null }>): Promise<PermitRenewal> {
    const response: any = await apiClient.patch(`/permits/renewals/${renewalId}`, payload);
    return response.data;
  }

  async deleteRenewal(renewalId: string): Promise<void> {
    await apiClient.delete(`/permits/renewals/${renewalId}`);
  }

  async runExpiryAlerts(thresholdDays = 30): Promise<{ checked: number; alertsCreated: number }> {
    const response: any = await apiClient.post('/permits/expiry-alerts/run', { thresholdDays });
    return response.data;
  }
}

export default new PermitsService();
